import { useCallback } from "react";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import {
  AppLayoutContext,
  appSelectionItemKey,
  type AppSelectionItem,
  type AppSelectionScope,
} from "@-/dev.improve.haniverse.routes.nav.app._context";

function selectionItemsMap(items: readonly AppSelectionItem[]) {
  return new Map(items.map((item) => [appSelectionItemKey(item), item]));
}

function selectionItemsScope(items: readonly AppSelectionItem[]): AppSelectionScope | null {
  return items[0]?.kind ?? null;
}

function assertSingleScope(items: readonly AppSelectionItem[]): AppSelectionScope | null {
  const scope = selectionItemsScope(items);
  if (scope !== null && items.some((item) => item.kind !== scope)) {
    throw new Error("A selection group must contain only one selection scope");
  }
  return scope;
}

/** Derived operations over the app-wide selection state held by {@link AppLayoutContext}. */
export function useAppSelectionActions() {
  // One subscription to a stable handle (see `useAppLayoutContextValue`): every selectable tile in a
  // grid calls this, so it must not subscribe to the scope and each setter separately.
  const [{ getSelectionScope, setIsSelecting, setSelectedItems, setSelectionScope }] =
    useContextSelectors(AppLayoutContext, (c) => c.selectionActions);

  // A long press always begins a fresh selection session with the pressed item/group.
  const startSelecting = useCallback(
    (items: readonly AppSelectionItem[]) => {
      const scope = assertSingleScope(items);
      if (scope === null) {
        return;
      }
      setSelectionScope(scope);
      setSelectedItems(selectionItemsMap(items));
      setIsSelecting(true);
    },
    [setIsSelecting, setSelectedItems, setSelectionScope],
  );

  // A group is one toggle target: if every item is selected, remove all; otherwise add all.
  // Crossing from a morpheme surface to a concrete-card surface replaces the session so bulk
  // actions never receive ambiguous mixed target kinds.
  const toggleSelected = useCallback(
    (items: readonly AppSelectionItem[]) => {
      const scope = assertSingleScope(items);
      if (scope === null) {
        return;
      }
      setIsSelecting(true);
      if (getSelectionScope() !== scope) {
        setSelectionScope(scope);
        setSelectedItems(selectionItemsMap(items));
        return;
      }
      setSelectedItems((previous) => {
        const keys = items.map((item) => appSelectionItemKey(item));
        const allSelected = keys.every((key) => previous.has(key));
        const next = new Map(previous);
        for (const [index, key] of keys.entries()) {
          if (allSelected) {
            next.delete(key);
          } else {
            const item = items[index];
            if (item !== undefined) {
              next.set(key, item);
            }
          }
        }
        return next;
      });
    },
    [getSelectionScope, setIsSelecting, setSelectedItems, setSelectionScope],
  );

  const stopSelecting = useCallback(() => {
    setIsSelecting(false);
    setSelectionScope(null);
    setSelectedItems(new Map());
  }, [setIsSelecting, setSelectedItems, setSelectionScope]);

  return { startSelecting, stopSelecting, toggleSelected };
}
